// Renders one turn's answer: collapsible steps, and pepsico-ds CardV2-based
// cards for Summary/Insights/Recommendations/Warnings/Chart/Table.

import React, { useState, useMemo } from 'react';
import { Font, Pagination } from 'pepsico-ds';
import CardV2 from '../../../v2/components/GDS/CardV2';
import ChartRenderer from './ChartRenderer';
import TableRenderer, { getColumnUnion } from '../components/TableRenderer';

function StepsPanel({ steps, statusMsg, error }) {
  const [expanded, setExpanded] = useState(false);
  const isDone = !statusMsg;
  const hasErrorStep = steps.some((s) => (s.status || '').toUpperCase() === 'ERROR');
  const totalSeconds = steps.reduce((sum, s) => sum + (s.durationMs || 0), 0) / 1000;

  const summaryLabel = !isDone
    ? statusMsg || 'Working…'
    : error
      ? 'Failed'
      : hasErrorStep
        ? `Completed in ${totalSeconds.toFixed(1)}s (some steps had issues)`
        : `Completed in ${totalSeconds.toFixed(1)}s`;

  return (
    <div className="ask-psd-v2-steps">
      <button
        type="button"
        className="ask-psd-v2-steps-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {!isDone && <span className="step-spinner" />}
        <span>{summaryLabel}</span>
        <span className="ask-psd-v2-steps-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <ul className="step-list" aria-label="Progress steps">
          {steps.map((step, i) => {
            const s = (step.status || '').toUpperCase();
            return (
              <li key={i} className={`step-item step-${s.toLowerCase()}`}>
                <div className="step-row">
                  <span className="step-icon">
                    {s === 'SUCCESS' && '✓'}
                    {s === 'ERROR' && '✕'}
                    {(s === 'RUNNING' || (s !== 'SUCCESS' && s !== 'ERROR')) && (
                      <span className="step-spinner"></span>
                    )}
                  </span>
                  <span className="step-name">{step.name}</span>
                  {step.durationMs > 0 && (
                    <span className="step-duration">{(step.durationMs / 1000).toFixed(1)}s</span>
                  )}
                </div>
                {s === 'ERROR' && step.errorMessage && (
                  <p className="step-error-detail">{step.errorMessage}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const COLLAPSED_ITEM_LIMIT = 2;

function CollapsibleList({ items }) {
  const [expanded, setExpanded] = useState(false);
  const overflowing = items.length > COLLAPSED_ITEM_LIMIT;
  const visibleItems = expanded || !overflowing ? items : items.slice(0, COLLAPSED_ITEM_LIMIT);

  return (
    <div className={`ask-psd-v2-collapsible ${!expanded && overflowing ? 'is-collapsed' : ''}`}>
      <ul className="ask-psd-v2-list">
        {visibleItems.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
      {overflowing && (
        <button type="button" className="ask-psd-v2-show-more" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `Show ${items.length - COLLAPSED_ITEM_LIMIT} more`}
        </button>
      )}
    </div>
  );
}

const PAGE_SIZE = 10;

function PaginatedTable({ data }) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / PAGE_SIZE));
  // Column set computed once from the FULL dataset (not the page slice), so
  // headers stay consistent across pages instead of shifting per row shape.
  const columns = useMemo(() => getColumnUnion(data), [data]);
  const pageData = useMemo(
    () => (data || []).slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [data, currentPage]
  );

  return (
    <div>
      <TableRenderer data={pageData} columns={columns} />
      {totalPages > 1 && (
        <div className="ask-psd-v2-pagination">
          <Pagination
            currentPage={currentPage}
            pageCount={totalPages}
            onUpdate={setCurrentPage}
            variant="number"
            size="medium"
          />
        </div>
      )}
    </div>
  );
}

export default function AnswerBlock({ turn }) {
  const { steps = [], statusMsg, error, response } = turn;

  return (
    <div className="ask-psd-v2-answer">
      {/* Resumed history turns have no step data (steps:[]) and no live
          statusMsg — showing the toggle then would just read "Completed in
          0.0s" and expand to an empty list, so only render it for turns
          that actually have something to show. */}
      {(steps.length > 0 || statusMsg) && (
        <StepsPanel steps={steps} statusMsg={statusMsg} error={error} />
      )}

      {error && (
        <p className="ask-psd-v2-error" role="alert">
          {error}
        </p>
      )}

      {response && (
        <>
          {response.summary && (
            <CardV2
              cardHeader={{ title: 'Summary' }}
              cardBody={{
                type: 'slot',
                slot: <Font size="body" className="ask-psd-v2-summary">{response.summary}</Font>,
              }}
            />
          )}

          {response.warnings?.length > 0 && (
            <CardV2
              cardHeader={{ title: 'Warnings' }}
              cardBody={{
                type: 'slot',
                slot: (
                  <ul className="ask-psd-v2-warning-list">
                    {response.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                ),
              }}
            />
          )}

          <div className="ask-psd-v2-card-grid">
            {response.insights?.length > 0 && (
              <CardV2
                cardHeader={{ title: 'Insights' }}
                cardBody={{
                  type: 'slot',
                  slot: <CollapsibleList items={response.insights} />,
                }}
              />
            )}

            {response.recommendations?.length > 0 && (
              <CardV2
                cardHeader={{ title: 'Recommendations' }}
                cardBody={{
                  type: 'slot',
                  slot: <CollapsibleList items={response.recommendations} />,
                }}
              />
            )}
          </div>

          {['BAR', 'LINE', 'GROUPED_BAR'].includes(response.chartType) &&
            response.chartConfig?.xaxis &&
            response.chartConfig?.yaxis &&
            (response.chartData?.length || response.data?.length) > 0 && (
              <ChartRenderer response={response} />
            )}

          <CardV2
            cardBody={{
              type: 'slot',
              slot: <PaginatedTable data={response.data} />,
            }}
          />

          {response.aiDisclaimer && (
            <p className="ask-psd-v2-disclaimer">{response.aiDisclaimer}</p>
          )}
        </>
      )}
    </div>
  );
}
